// Mock micro-unlock content for the support/unlock prototype. Stands in for a
// MemeGenes content-access API until the real 402 content route is wired.
// Provny is more than images — this seeds articles, photos, and video.
// Images via picsum (dev only); reuses the same img() shape as mock.ts.

export type ContentType = 'article' | 'photo' | 'video';

/** The free-to-browse teaser shown before a reader backs the creator. */
type Preview =
  | { type: 'article'; dek: string; lede: string }
  | { type: 'photo'; image: string }
  | { type: 'video'; poster: string; duration: string };

/** The full work, revealed in place after the unlock settles. */
type Body =
  | { type: 'article'; markdown: string; image?: string }
  | { type: 'photo'; image: string }
  | { type: 'video'; poster: string; duration: string };

export type Content = {
  id: string;
  type: ContentType;
  channel: string;
  channelSlug: string;
  creator: string; // handle, no leading "@"
  title: string;
  preview: Preview;
  body: Body;
  priceCents: number; // small — the cost of backing the creator (1–10¢)
  geneId: string; // links out to memegenes.com/gene/<geneId>
};

const img = (seed: string, w: number, h: number) =>
  `https://picsum.photos/seed/${seed}/${w}/${h}`;

export const content: Content[] = [
  {
    id: 'c1',
    type: 'article',
    channel: 'Citizen Journalism',
    channelSlug: 'citizen-journalism',
    creator: 'onthewire',
    title: 'The Night the Power Stayed On',
    preview: {
      type: 'article',
      dek: 'A neighbourhood wired its own grid before the city could. This is how.',
      lede: 'The lights went out at 9:14pm, the way they had every storm that winter. By 9:40, one block on the east side was the only thing glowing for a mile. Nobody from the utility had shown up. They never did.',
    },
    body: {
      type: 'article',
      markdown:
        "The lights went out at 9:14pm, the way they had every storm that winter. By 9:40, one block on the east side was the only thing glowing for a mile. Nobody from the utility had shown up. They never did.\n\nWhat the block had instead was Marisol Vega, a retired lineworker, and a garage full of salvaged batteries.\n\n**\"People kept saying we needed permission,\"** she told me, standing under a string of lights she'd run herself. **\"Permission from who? The dark?\"**\n\nOver eighteen months, the block pooled what it had — solar off three roofs, two old EV packs, a controller Vega rebuilt by hand. When the next storm hit, they stayed lit while the grid blinked out around them.\n\nThe city has since sent two inspectors and one cease-and-desist. The lights are still on.",
      image: img('art-grid', 1200, 700),
    },
    priceCents: 4,
    geneId: 'gen_w1a02',
  },
  {
    id: 'c2',
    type: 'photo',
    channel: 'Street Photography',
    channelSlug: 'street-photography',
    creator: 'streetshooter',
    title: 'Rooftop, Last Train',
    preview: { type: 'photo', image: img('px-roof', 900, 1100) },
    body: { type: 'photo', image: img('px-roof', 1600, 2000) },
    priceCents: 3,
    geneId: 'gen_p2b41',
  },
  {
    id: 'c3',
    type: 'video',
    channel: 'Music Drops',
    channelSlug: 'music-drops',
    creator: 'wax.fm',
    title: 'First Take, No Overdubs',
    preview: { type: 'video', poster: img('vid-studio', 1200, 675), duration: '3:48' },
    body: { type: 'video', poster: img('vid-studio', 1600, 900), duration: '3:48' },
    priceCents: 6,
    geneId: 'gen_v3c19',
  },
  {
    id: 'c4',
    type: 'article',
    channel: 'Meme Culture',
    channelSlug: 'meme-culture',
    creator: 'b_roll',
    title: 'Anatomy of a Repost',
    preview: {
      type: 'article',
      dek: 'One frame, four million reposts, zero credit. We traced it back to the source.',
      lede: 'It started, as these things do, with a screenshot of a screenshot. By the time the frame had crossed three platforms it had lost its creator, its context, and most of its pixels.',
    },
    body: {
      type: 'article',
      markdown:
        "It started, as these things do, with a screenshot of a screenshot. By the time the frame had crossed three platforms it had lost its creator, its context, and most of its pixels.\n\nWhat it never lost — though nobody could see it — was its proof of origin.\n\n**The repost chain:** day one, a small account posts the original. Day three, a meme page lifts it, crops the watermark that was never there, and adds a caption. Day five, a verified account with two million followers reposts the meme page's version. The creator finds out from a friend.\n\nWe ran the viral frame against its Gene. It matched the original on the first try — same source, same creator, traceable across every degraded copy. The credit was always there. The platforms just never looked.",
    },
    priceCents: 3,
    geneId: 'gen_w4d77',
  },
  {
    id: 'c5',
    type: 'photo',
    channel: 'Sneaker Culture',
    channelSlug: 'sneaker-culture',
    creator: 'soleau',
    title: 'Unboxing, 6am',
    preview: { type: 'photo', image: img('px-shoe', 900, 1100) },
    body: { type: 'photo', image: img('px-shoe', 1600, 2000) },
    priceCents: 2,
    geneId: 'gen_p5e63',
  },
  {
    id: 'c6',
    type: 'video',
    channel: 'Street Photography',
    channelSlug: 'street-photography',
    creator: 'nightframe',
    title: 'Blue Hour, One Take',
    preview: { type: 'video', poster: img('vid-night', 1200, 675), duration: '1:52' },
    body: { type: 'video', poster: img('vid-night', 1600, 900), duration: '1:52' },
    priceCents: 5,
    geneId: 'gen_v6f08',
  },
  {
    id: 'c7',
    type: 'article',
    channel: 'Digital Art',
    channelSlug: 'digital-art',
    creator: 'pixelwitch',
    title: 'Drawing by Hand in a Generated World',
    preview: {
      type: 'article',
      dek: 'Why one artist still inks every frame, one obsolete pixel at a time.',
      lede: 'There is a particular quiet to drawing a frame you know a machine could fake in a second. Pixelwitch leans into it. She times herself: forty minutes a frame, no shortcuts.',
    },
    body: {
      type: 'article',
      markdown:
        "There is a particular quiet to drawing a frame you know a machine could fake in a second. Pixelwitch leans into it. She times herself: forty minutes a frame, no shortcuts.\n\n**\"The point isn't that you can't tell,\"** she says. **\"The point is that I can.\"**\n\nHer editions are small on purpose — ten, fifteen at most. Each one carries proof it came from her hand and her hours. In a feed full of frictionless output, the friction is the work.",
      image: img('art-ink', 1200, 700),
    },
    priceCents: 4,
    geneId: 'gen_w7a55',
  },
  {
    id: 'c8',
    type: 'photo',
    channel: 'Citizen Journalism',
    channelSlug: 'citizen-journalism',
    creator: 'fieldnote',
    title: 'From the Front of the Crowd',
    preview: { type: 'photo', image: img('px-march', 900, 1100) },
    body: { type: 'photo', image: img('px-march', 1600, 2000) },
    priceCents: 3,
    geneId: 'gen_p8b22',
  },
];

/** Look up a single content item by id — stands in for a GET /content/:id query. */
export function getContent(id: string): Content | undefined {
  return content.find((c) => c.id === id);
}
